import React from 'react';
import '../styles/SkillAssessment.css';

const SkillAssessmentRenderer = ({ assessmentData }) => {
  if (!assessmentData) return null;
  
  // Extract data from both old and new structure
  const { 
    // New structure
    criticalSkills, 
    goodToHaveSkills,
    // Backward compatibility
    technicalSkills, 
    softSkills, 
    experienceAlignment, 
    keyStrengths, 
    developmentAreas, 
    overallRecommendation 
  } = assessmentData;
  
  // Function to render a rating as stars
  const renderRatingStars = (rating) => {
    const maxRating = 5;
    return (
      <div className="rating-stars">
        {Array(maxRating).fill().map((_, i) => (
          <span 
            key={i} 
            className={`star ${i < rating ? 'filled' : 'empty'}`}
          >
            ★
          </span>
        ))}
        <span className="rating-number">{rating}/5</span>
      </div>
    );
  };

  // Function to render skill items with priority indicator
  const renderSkillItem = (skill, index, skillType, priority) => (
    <div className={`skill-item ${priority ? priority.toLowerCase() : ''}`} key={`${skillType}-${index}`}>
      <div className="skill-header">
        <h4 className="skill-name">
          {skill.skill}
          {priority && <span className={`priority-badge ${priority.toLowerCase()}`}>{priority}</span>}
        </h4>
        {renderRatingStars(skill.rating)}
      </div>
      {skill.evidence && (
        <div className="skill-evidence">
          <span className="evidence-label">Evidence:</span> {skill.evidence}
        </div>
      )}
      {skill.wasAssessed === false && (
        <div className="not-assessed-indicator">
          <span className="not-assessed-text">Not assessed during interview</span>
        </div>
      )}
    </div>
  );

  // Determine which structure to use
  const useNewStructure = criticalSkills || goodToHaveSkills;

  return (
    <div className="skill-assessment-container">
      {/* NEW STRUCTURE: Critical and Good-to-Have Skills */}
      {useNewStructure ? (
        <>
          {/* Critical Technical Skills */}
          {criticalSkills?.technicalSkills && criticalSkills.technicalSkills.length > 0 && (
            <section className="assessment-section critical-technical-skills">
              <h3>Critical Technical Skills Assessment</h3>
              <div className="skills-list">
                {criticalSkills.technicalSkills.map((skill, index) => 
                  renderSkillItem(skill, index, 'critical-tech', 'Critical')
                )}
              </div>
            </section>
          )}

          {/* Good-to-Have Technical Skills */}
          {goodToHaveSkills?.technicalSkills && goodToHaveSkills.technicalSkills.length > 0 && (
            <section className="assessment-section optional-technical-skills">
              <h3>Optional Technical Skills Assessment</h3>
              <div className="skills-list">
                {goodToHaveSkills.technicalSkills.map((skill, index) => 
                  renderSkillItem(skill, index, 'optional-tech', 'Optional')
                )}
              </div>
            </section>
          )}

          {/* Critical Soft Skills */}
          {criticalSkills?.softSkills && criticalSkills.softSkills.length > 0 && (
            <section className="assessment-section critical-soft-skills">
              <h3>Critical Soft Skills Assessment</h3>
              <div className="skills-list">
                {criticalSkills.softSkills.map((skill, index) => 
                  renderSkillItem(skill, index, 'critical-soft', 'Critical')
                )}
              </div>
            </section>
          )}

          {/* Good-to-Have Soft Skills */}
          {goodToHaveSkills?.softSkills && goodToHaveSkills.softSkills.length > 0 && (
            <section className="assessment-section optional-soft-skills">
              <h3>Optional Soft Skills Assessment</h3>
              <div className="skills-list">
                {goodToHaveSkills.softSkills.map((skill, index) => 
                  renderSkillItem(skill, index, 'optional-soft', 'Optional')
                )}
              </div>
            </section>
          )}
        </>
      ) : (
        <>
          {/* FALLBACK: Old Structure */}
          {/* Technical Skills Section */}
          {technicalSkills && technicalSkills.length > 0 && (
            <section className="assessment-section technical-skills">
              <h3>Technical Skills Assessment</h3>
              <div className="skills-list">
                {technicalSkills.map((skill, index) => 
                  renderSkillItem(skill, index, 'tech', null)
                )}
              </div>
            </section>
          )}
          
          {/* Soft Skills Section */}
          {softSkills && softSkills.length > 0 && (
            <section className="assessment-section soft-skills">
              <h3>Soft Skills Assessment</h3>
              <div className="skills-list">
                {softSkills.map((skill, index) => 
                  renderSkillItem(skill, index, 'soft', null)
                )}
              </div>
            </section>
          )}
        </>
      )}
      
      {/* Experience Alignment - unchanged */}
      {experienceAlignment && (
        <section className="assessment-section experience-alignment">
          <h3>Experience Alignment</h3>
          <div className="experience-details">
            <div className="experience-rating">
              <span className="rating-label">Overall Rating:</span> 
              {renderRatingStars(experienceAlignment.rating)}
            </div>
            {experienceAlignment.notes && (
              <div className="experience-notes">{experienceAlignment.notes}</div>
            )}
          </div>
        </section>
      )}
      
      {/* Summary Section - unchanged */}
      <section className="assessment-section summary">
        <div className="summary-columns">
          {/* Key Strengths */}
          {keyStrengths && keyStrengths.length > 0 && (
            <div className="summary-column strengths">
              <h3>Key Strengths</h3>
              <ul className="summary-list">
                {keyStrengths.map((strength, index) => (
                  <li key={`strength-${index}`}>{strength}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Development Areas */}
          {developmentAreas && developmentAreas.length > 0 && (
            <div className="summary-column development">
              <h3>Areas for Development</h3>
              <ul className="summary-list">
                {developmentAreas.map((area, index) => (
                  <li key={`area-${index}`}>{area}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Recommendation */}
        {overallRecommendation && (
          <div className="recommendation">
            <h3>Overall Recommendation</h3>
            <p>{overallRecommendation}</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default SkillAssessmentRenderer;